import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faSpinner, faEnvelope, faRocket } from '@fortawesome/free-solid-svg-icons';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import { APIRoutes } from '@/services/api.routes.ts';
import { showSuccessToast, showErrorToast, handleAPIError } from '@/utils/toasts.ts';
import type { VideoID, Video } from '@/models';
import { FILE_SIZE_LIMITS, formatFileSize } from '@/config';
import Checkbox from '@/components/ui/Checkbox';
import CardCheckbox from '@/components/ui/CardCheckbox';

interface VideoUploadButtonProps {
  videoId: VideoID;
  onVideoUpdated?: (video: Video) => void;
  onUploadingChange?: (uploading: boolean) => void;
  disabled?: boolean;
  className?: string;
  moduleNoKnowledge?: boolean;
}

const VideoUploadButton: React.FC<VideoUploadButtonProps> = ({
  videoId,
  onVideoUpdated,
  onUploadingChange,
  disabled = false,
  className = '',
  moduleNoKnowledge = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [createKnowledge, setCreateKnowledge] = useState(!moduleNoKnowledge);
  const [autoPublish, setAutoPublish] = useState(false);
  const [autoPublishSendDiscord, setAutoPublishSendDiscord] = useState(true);
  const [autoPublishSendTelegram, setAutoPublishSendTelegram] = useState(true);
  const [autoPublishSendMail, setAutoPublishSendMail] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Notify parent about upload state and warn user before leaving page during upload
  useEffect(() => {
    if (!uploading) return;

    onUploadingChange?.(true);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return ''; // Modern browsers show their own message
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      onUploadingChange?.(false);
    };
  }, [uploading, onUploadingChange]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|avi|webm)$/i)) {
      showErrorToast('Nur Video-Dateien sind erlaubt (MP4, MOV, AVI, WebM)');
      return;
    }

    // Check file size
    if (file.size > FILE_SIZE_LIMITS.VIDEO_MAX) {
      showErrorToast(`Video-Datei ist zu groß (max. ${formatFileSize(FILE_SIZE_LIMITS.VIDEO_MAX)})`);
      return;
    }

    setSelectedFile(file);
    setShowModal(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setShowModal(false);
    setUploading(true);
    setUploadProgress(0);

    try {
      const updatedVideo = await APIRoutes.uploadVideo(
        videoId,
        selectedFile,
        setUploadProgress,
        createKnowledge,
        autoPublish,
        autoPublishSendDiscord,
        autoPublishSendMail,
        autoPublishSendTelegram
      );

      console.log('VideoUploadButton: Upload response:', updatedVideo);

      if (!updatedVideo) {
        console.error('VideoUploadButton: uploadVideo returned undefined');
        showErrorToast('Fehler: Unvollständige Antwort vom Server');
        return;
      }

      showSuccessToast('Video erfolgreich hochgeladen');
      onVideoUpdated?.(updatedVideo);
    } catch (error) {
      console.error('Video upload error:', error);
      handleAPIError(error, 'Fehler beim Hochladen des Videos');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setSelectedFile(null);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleButtonClick = () => {
    if (!uploading && !disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm"
        style={{ display: 'none' }}
      />
      <button
        onClick={handleButtonClick}
        disabled={uploading || disabled}
        className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer ${
          uploading || disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${className}`}
      >
        <FontAwesomeIcon
          icon={uploading ? faSpinner : faUpload}
          className={`w-4 h-4 ${uploading ? 'animate-spin' : ''}`}
        />
        {uploading ? (
          <span>Hochladen... {uploadProgress}%</span>
        ) : (
          <span>Video hochladen</span>
        )}
      </button>

      {/* Upload Options Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Video-Upload-Optionen
            </h3>

            {/* Knowledge Option */}
            <div className="mb-4">
              <Checkbox
                checked={createKnowledge}
                onChange={setCreateKnowledge}
                disabled={moduleNoKnowledge}
              >
                <span className={`text-sm ${moduleNoKnowledge ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                  Knowledge automatisch erstellen
                </span>
              </Checkbox>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-7">
                Wenn aktiviert, wird das Video automatisch verarbeitet und Knowledge-Daten extrahiert.
              </p>
              {moduleNoKnowledge && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 ml-7">
                  Videos in diesem Modul sind von die KI-Analyse für den Chat ausgeschlossen
                </p>
              )}
            </div>

            {/* Auto-Publish Option */}
            <div className="mb-4 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
              <Checkbox
                checked={autoPublish}
                onChange={setAutoPublish}
              >
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faRocket} className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Automatisch veröffentlichen
                  </span>
                </div>
              </Checkbox>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-7">
                Wenn aktiviert, wird das Video nach Abschluss der Verarbeitung automatisch veröffentlicht.
              </p>

              {/* Nested notification options - always visible, disabled when autoPublish is not checked */}
              <div className={`mt-4 ml-7 space-y-3 ${!autoPublish ? 'opacity-50' : ''}`}>
                <CardCheckbox
                  checked={autoPublishSendDiscord}
                  onChange={setAutoPublishSendDiscord}
                  disabled={!autoPublish}
                  padding="sm"
                >
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faDiscord} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Discord-Nachricht senden</span>
                  </div>
                </CardCheckbox>

                <CardCheckbox
                  checked={autoPublishSendTelegram}
                  onChange={setAutoPublishSendTelegram}
                  disabled={!autoPublish}
                  padding="sm"
                >
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faTelegram} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Telegram-Nachricht senden</span>
                  </div>
                </CardCheckbox>

                <CardCheckbox
                  checked={autoPublishSendMail}
                  onChange={setAutoPublishSendMail}
                  disabled={!autoPublish}
                  padding="sm"
                >
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">E-Mail-Benachrichtigung senden</span>
                  </div>
                </CardCheckbox>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
              >
                Abbrechen
              </button>
              <button
                onClick={handleUpload}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
              >
                Hochladen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoUploadButton;